// client/src/components/Reports/ReportsPage.js
// Full-page financial reports: Cash Flow (Sankey), Year-over-Year, Unusual Spending.
// Uses recharts BarChart for YoY (already in package.json), CSS Sankey for cash flow,
// and a clean MUI list for unusual spending.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { TrendingUp, BarChart2, AlertTriangle, Info } from 'lucide-react';
import SankeyChart from './SankeyChart';
import api from '../../services/api';
import { useInsightsFilter } from '../Insights/shared/InsightsFilterContext';
import InsightsDateFilter from '../Insights/shared/InsightsDateFilter';

// ── Constants ─────────────────────────────────────────────────────────────────

const TABS = [
  { label: '💸 Cash Flow',         icon: TrendingUp   },
  { label: '📊 Year-over-Year',    icon: BarChart2    },
  { label: '🚨 Unusual Spending',  icon: AlertTriangle },
];

const CURRENCY = '€';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toMonthInput(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthInputToDate(value) {
  const [y, m] = value.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
  );
}

function LoadingState() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
      <CircularProgress size={36} />
    </Box>
  );
}

function ErrorState({ message }) {
  return <Alert severity="error" sx={{ mt: 2 }}>{message || 'Failed to load data.'}</Alert>;
}

// ─── Tab 1: Cash Flow (Sankey) ────────────────────────────────────────────────

function CashFlowTab() {
  const { from, to } = useInsightsFilter();
  const [data,   setData  ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError  ] = useState(null);

  const fetch = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/insights-extended/sankey', { params: { from, to } });
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  // Re-fetch whenever the global date range changes
  useEffect(() => { fetch(); }, [fetch]);

  return (
    <Box>
      <SectionHeader
        title="Cash Flow"
        subtitle="See how money flows from income sources through spending categories to individual merchants."
      />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} />}

      {!loading && !error && data && (
        <>
          {/* Summary chips */}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`Total Income: ${CURRENCY}${(data.meta?.totalIncome || 0).toLocaleString()}`} color="success" size="small" />
            <Chip label={`Total Expenses: ${CURRENCY}${(data.meta?.totalExpenses || 0).toLocaleString()}`} color="error" size="small" />
          </Stack>

          <SankeyChart nodes={data.nodes} links={data.links} currency={CURRENCY} />
        </>
      )}
    </Box>
  );
}

// ─── Tab 2: Year-over-Year ────────────────────────────────────────────────────

const YOY_COLORS = { year1: '#1976d2', year2: '#4caf50', positive: '#f44336', negative: '#4caf50' };

function YoYTab() {
  const { to } = useInsightsFilter();
  const [year1, setYear1] = useState(() => String(new Date(to).getFullYear() - 1));
  const [year2, setYear2] = useState(() => String(new Date(to).getFullYear()));
  const [data,   setData  ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError  ] = useState(null);

  // Sync year inputs when global date range changes
  useEffect(() => {
    const y = new Date(to).getFullYear();
    if (!isNaN(y)) {
      setYear2(String(y));
      setYear1(String(y - 1));
    }
  }, [to]);

  const fetch = useCallback(async () => {
    if (!year1 || !year2 || year1 === year2) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/insights-extended/yoy', { params: { year1, year2 } });
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [year1, year2]);

  // Auto-fetch whenever year1/year2 change (driven by global filter or manual edit)
  useEffect(() => { fetch(); }, [fetch]);

  const chartData = data
    ? data.categories.slice(0, 12).map(c => ({
        name:     c.name.length > 14 ? c.name.slice(0, 12) + '…' : c.name,
        fullName: c.name,
        [year1]:  c.year1Total,
        [year2]:  c.year2Total,
        change:   c.changePercent,
        color:    c.color,
      }))
    : [];

  return (
    <Box>
      <SectionHeader
        title="Year-over-Year Comparison"
        subtitle="Compare category spending between two calendar years."
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <TextField
          label="Year 1"
          type="number"
          size="small"
          value={year1}
          onChange={e => setYear1(e.target.value)}
          inputProps={{ min: 2000, max: 2099 }}
          sx={{ width: 120 }}
        />
        <TextField
          label="Year 2"
          type="number"
          size="small"
          value={year2}
          onChange={e => setYear2(e.target.value)}
          inputProps={{ min: 2000, max: 2099 }}
          sx={{ width: 120 }}
        />
        <Button variant="contained" size="small" onClick={fetch} disabled={loading}>
          {loading ? 'Loading…' : 'Compare'}
        </Button>
      </Stack>

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} />}

      {!loading && !error && chartData.length > 0 && (
        <>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                angle={-35}
                textAnchor="end"
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={v => `${CURRENCY}${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
              />
              <RechartsTooltip
                formatter={(value, name) => [`${CURRENCY}${value.toLocaleString()}`, name]}
                contentStyle={{ borderRadius: 8, fontSize: 12 }}
              />
              <Legend wrapperStyle={{ paddingTop: 16, fontSize: 12 }} />
              <Bar dataKey={year1} fill={YOY_COLORS.year1} radius={[3, 3, 0, 0]} name={`${year1}`} />
              <Bar dataKey={year2} fill={YOY_COLORS.year2} radius={[3, 3, 0, 0]} name={`${year2}`} />
            </BarChart>
          </ResponsiveContainer>

          {/* Change table */}
          <Box sx={{ mt: 3, overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
            >
              <thead>
                <Box
                  component="tr"
                  sx={{ borderBottom: '2px solid', borderColor: 'divider', textAlign: 'left' }}
                >
                  {['Category', year1, year2, 'Change'].map(h => (
                    <Box component="th" key={h} sx={{ py: 1, px: 1.5, fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </thead>
              <tbody>
                {data.categories.map(c => {
                  const up = c.change > 0;
                  return (
                    <Box
                      component="tr"
                      key={c.name}
                      sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Box component="td" sx={{ py: 1, px: 1.5 }}>{c.name}</Box>
                      <Box component="td" sx={{ py: 1, px: 1.5 }}>{CURRENCY}{c.year1Total.toLocaleString()}</Box>
                      <Box component="td" sx={{ py: 1, px: 1.5 }}>{CURRENCY}{c.year2Total.toLocaleString()}</Box>
                      <Box
                        component="td"
                        sx={{
                          py: 1, px: 1.5, fontWeight: 600,
                          color: up ? 'error.main' : 'success.main',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {up ? '+' : ''}{CURRENCY}{c.change.toLocaleString()}
                        {c.changePercent !== null && (
                          <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                            ({up ? '+' : ''}{c.changePercent}%)
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        </>
      )}

      {!loading && !error && data && chartData.length === 0 && (
        <Alert severity="info">No expense data found for the selected years.</Alert>
      )}
    </Box>
  );
}

// ─── Tab 3: Unusual Spending ──────────────────────────────────────────────────

function UnusualSpendingTab() {
  const { from, to } = useInsightsFilter();
  const [data,   setData  ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError  ] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/insights-extended/unusual-spending', { params: { from, to } });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to]);

  return (
    <Box>
      <SectionHeader
        title="Unusual Spending"
        subtitle="Categories where this month's spending exceeds 150% of your 3-month rolling average."
      />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} />}

      {!loading && !error && data && (
        <>
          {data.data.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              🎉 No unusual spending detected this month — your spending looks normal!
            </Alert>
          ) : (
            <Stack spacing={2}>
              {data.data.map((item, idx) => {
                const severity = item.percent >= 100 ? 'error' : 'warning';
                const barValue = Math.min(100, (item.currentMonth / (item.rollingAvg * 2.5)) * 100);

                return (
                  <Card
                    key={idx}
                    variant="outlined"
                    sx={{
                      borderColor: severity === 'error' ? 'error.main' : 'warning.main',
                      borderWidth: 1.5,
                    }}
                  >
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {item.category}
                        </Typography>
                        <Chip
                          label={`+${item.percent}%`}
                          color={severity}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">This month</Typography>
                          <Typography variant="body2" fontWeight={700} color={`${severity}.main`}>
                            {CURRENCY}{item.currentMonth.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">3-mo avg</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {CURRENCY}{item.rollingAvg.toLocaleString()}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Visual bar */}
                      <Box sx={{ position: 'relative' }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (item.rollingAvg / item.currentMonth) * 100)}
                          color="success"
                          sx={{ height: 8, borderRadius: 4, bgcolor: `${severity}.light` }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {item.message}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          {data.meta && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Analysis for {data.meta.month} · Threshold: {data.meta.threshold} of {data.meta.rollingMonths}-month average
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);

  const TAB_COMPONENTS = [CashFlowTab, YoYTab, UnusualSpendingTab];
  const ActiveTab = TAB_COMPONENTS[activeTab];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Page header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 60%, #01579b 100%)',
          borderRadius: 3,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)' }} />
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={900} sx={{ letterSpacing: -0.5, position: 'relative', zIndex: 1 }}>
          📈 Reports
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5, position: 'relative', zIndex: 1 }}>
          Deep-dive analytics: cash flow, year-over-year trends, and spending alerts.
        </Typography>
      </Box>

      {/* Global Insights date-range filter */}
      <InsightsDateFilter />

      {/* Tab navigation */}
      <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600, minHeight: 52 },
          }}
        >
          {TABS.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </Tabs>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <ActiveTab />
        </CardContent>
      </Card>
    </Container>
  );
}
